/**
 * Transforms JSON:API formatted webinar response to the old schema format
 *
 * New format example:
 * {
 *   webinars: {
 *     data: [{ id, type, attributes, relationships }],
 *     included: [{ id, type, attributes }]
 *   },
 *   pages: 1,
 *   count: 3
 * }
 *
 * Old format example:
 * {
 *   webinars: [{ id, webinar_title, experts: [...], sponsors: [...], ... }],
 *   pages: 1,
 *   count: 3
 * }
 */

import { sortExpertsByNumeration } from "./sort-experts";

/**
 * Transform a JSON:API response to the old webinar schema
 * @param {Object} response - Axios response object
 * @returns {Object} - Response with transformed data
 */
export const transformWebinarResponse = (response) => {
  // If the response doesn't have the new format, return as is
  if (!response.data.webinars || !response.data.webinars.data) {
    return response;
  }

  const { webinars, pages, count, years, cpd_points, total_pages, terms } =
    response.data;
  const { data, included = [] } = webinars;

  // Create a map of included resources by type and id for quick lookup
  const includedMap = {};
  included.forEach((resource) => {
    const type = resource.type;
    if (!includedMap[type]) {
      includedMap[type] = {};
    }
    includedMap[type][resource.id] = resource.attributes;
  });

  // Transform each webinar
  const transformedWebinars = data.map((webinarData) => {
    const { id, type, attributes, relationships } = webinarData;

    // Start with the flattened attributes
    const webinar = {
      ...attributes,
    };

    // Process relationships
    if (relationships) {
      // Process experts
      if (relationships.experts && relationships.experts.data) {
        webinar.experts = sortExpertsByNumeration(
          relationships.experts.data.map((expertRef) => {
            const expert =
              (includedMap["expert"] && includedMap["expert"][expertRef.id]) ||
              {};
            return {
              id: parseInt(expertRef.id),
              ...expert,
            };
          })
        );
      }

      // Process sponsors
      if (relationships.sponsors && relationships.sponsors.data) {
        webinar.sponsors = relationships.sponsors.data.map((sponsorRef) => {
          const sponsor =
            (includedMap["sponsor"] && includedMap["sponsor"][sponsorRef.id]) ||
            {};
          return {
            id: parseInt(sponsorRef.id),
            ...sponsor,
          };
        });
      }

      // Process partnerships
      if (relationships.partnerships && relationships.partnerships.data) {
        webinar.partnerships = relationships.partnerships.data.map(
          (partnerRef) => {
            const partnership =
              (includedMap["partnership"] &&
                includedMap["partnership"][partnerRef.id]) ||
              {};
            return {
              id: parseInt(partnerRef.id),
              ...partnership,
            };
          }
        );
      }

      // Process galery_images
      if (relationships.galery_images && relationships.galery_images.data) {
        webinar.galery_images = relationships.galery_images.data.map(
          (imageRef) => {
            const galery_image =
              (includedMap["galery_image"] &&
                includedMap["galery_image"][imageRef.id]) ||
              {};
            return {
              id: parseInt(imageRef.id),
              ...galery_image,
            };
          }
        );
      }
    }

    return webinar;
  });

  // Build the transformed response in old format
  const transformedData = {
    webinars: transformedWebinars,
  };

  // Add pagination and other metadata if present
  if (pages !== undefined) transformedData.pages = pages;
  if (count !== undefined) transformedData.count = count;
  if (years !== undefined) transformedData.years = years;
  if (cpd_points !== undefined) transformedData.cpd_points = cpd_points;
  if (total_pages !== undefined) transformedData.total_pages = total_pages;
  if (terms !== undefined) transformedData.terms = terms;

  return {
    ...response,
    data: transformedData,
  };
};

/**
 * Transform a single webinar JSON:API response to the old schema
 * @param {Object} response - Axios response object
 * @returns {Object} - Response with transformed data
 */
export const transformSingleWebinarResponse = (response) => {
  // If the response doesn't have the new format, return as is
  if (!response.data.webinar || !response.data.webinar.data) {
    return response;
  }

  const { webinar } = response.data;
  const { data, included = [] } = webinar;

  // Create a map of included resources by type and id for quick lookup
  const includedMap = {};
  included.forEach((resource) => {
    const type = resource.type;
    if (!includedMap[type]) {
      includedMap[type] = {};
    }
    includedMap[type][resource.id] = resource.attributes;
  });

  // Transform the webinar
  const { id, type, attributes, relationships } = data;

  const transformedWebinar = {
    ...attributes,
  };

  // Process relationships
  if (relationships) {
    // Process experts
    if (relationships.experts && relationships.experts.data) {
      transformedWebinar.experts = sortExpertsByNumeration(
        relationships.experts.data.map((expertRef) => {
          const expert =
            (includedMap["expert"] && includedMap["expert"][expertRef.id]) ||
            {};
          return {
            id: parseInt(expertRef.id),
            ...expert,
          };
        })
      );
    }

    // Process sponsors
    if (relationships.sponsors && relationships.sponsors.data) {
      transformedWebinar.sponsors = relationships.sponsors.data.map(
        (sponsorRef) => {
          const sponsor =
            (includedMap["sponsor"] && includedMap["sponsor"][sponsorRef.id]) ||
            {};
          return {
            id: parseInt(sponsorRef.id),
            ...sponsor,
          };
        }
      );
    }

    // Process partnerships
    if (relationships.partnerships && relationships.partnerships.data) {
      transformedWebinar.partnerships = relationships.partnerships.data.map(
        (partnerRef) => {
          const partnership =
            (includedMap["partnership"] &&
              includedMap["partnership"][partnerRef.id]) ||
            {};
          return {
            id: parseInt(partnerRef.id),
            ...partnership,
          };
        }
      );
    }

    // Process galery_images
    if (relationships.galery_images && relationships.galery_images.data) {
      transformedWebinar.galery_images = relationships.galery_images.data.map(
        (imageRef) => {
          const galery_image =
            (includedMap["galery_image"] &&
              includedMap["galery_image"][imageRef.id]) ||
            {};
          return {
            id: parseInt(imageRef.id),
            ...galery_image,
          };
        }
      );
    }
  }

  return {
    ...response,
    data: {
      webinar: transformedWebinar,
    },
  };
};



// WEBPACK FOOTER //
// ./src/utils/webinar-response-transformer.js